import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Typography,
  Tabs,
  Tab,
  Switch,
  Select,
  MenuItem,
  Checkbox,
  Menu,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import {
  createBranch,
  updateBranch,
  updateBranchCloseDays,
  getBranchCloseDays,
  updateBranchEntranceTime,
  getBranchEntranceTime,
} from '../../api/center-branch';
import { toggleBranchExposure } from '../../api/exposure';
import ConfirmModal from '../common/modal/ConfirmModal';
import { getCoordinatesByAddress } from '../../api/nominatium';
import { facilities, scriptUrl } from '../../constants/constants';
import FormInputCEditor from '../../form-components/FormInputCEditor';
import { FormInputMultiCheckbox } from '../../form-components/FormInputMultiCheckbox';
import { FormInputText } from '../../form-components/FormInputText';
import { errorMessages } from '../../utils/methods';
import CustomCheckbox from '../CustomCheckbox';
import DeleteForeverIcon from '@mui/icons-material/DeleteOutline';
import { imageValidation } from '../../utils/methods';
import { useNavigate } from 'react-router-dom';
import StoreUploadModal from './StoreUploadModal';
import EntranceTimeModal, { isOvernight } from './EntranceTimeModal/EntranceTimeModal';
import { NextDayBadge } from './EntranceTimeModal/EntranceTimeModalStyled';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';

// 대표 운동 종목 정의 — 항목 추가/삭제 시 이 배열만 수정
const SPORT_TYPES = [
  { key: 'fitness', enumKey: 'FITNESS', label: '피트니스 / P.T', width: 140 },
  { key: 'pilates', enumKey: 'PILATES', label: '필라테스', width: 100 },
  { key: 'barre_pilates', enumKey: 'BARRE_PILATES', label: '바레 필라테스', width: 120 },
  { key: 'golf', enumKey: 'GOLF', label: '골프', width: 80 },
  { key: 'yoga', enumKey: 'YOGA', label: '요가', width: 80 },
  { key: 'crossfit', enumKey: 'CROSSFIT', label: '크로스핏', width: 100 },
  { key: 'ball_games', enumKey: 'BALL_GAMES', label: '구기 / 라켓', width: 110 },
  { key: 'boxing', enumKey: 'BOXING', label: '복싱 / 격투', width: 110 },
  { key: 'dance', enumKey: 'DANCE', label: '댄스', width: 80 },
  { key: 'swimming', enumKey: 'SWIMMING', label: '수영 / 수상', width: 110 },
  { key: 'cycle', enumKey: 'CYCLE', label: '사이클', width: 80 },
  { key: 'education', enumKey: 'EDUCATION', label: '교육', width: 80 },
  { key: 'tanning', enumKey: 'TANNING', label: '태닝', width: 80 },
  { key: 'stretching', enumKey: 'STRETCHING', label: '스트레칭', width: 100 },
];

function StoreTab2({ branchDetailInfo, setSelectedTab, branchId, centerDetailInfo, managerInfo }) {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [sportTypes, setSportTypes] = useState([]);
  const [finalPrice, setFinalPrice] = useState(0);
  const [lastChangedField, setLastChangedField] = useState(null);
  const [subTab, setSubTab] = useState(0);
  const [entranceModalOpen, setEntranceModalOpen] = useState(false);
  const [entranceSetting, setEntranceSetting] = useState(null);
  const [exposureYn, setExposureYn] = useState(branchDetailInfo?.exposureYn || 'N');
  const [exposureConfirmOpen, setExposureConfirmOpen] = useState(false);

  useEffect(() => {
    if (branchDetailInfo?.exposureYn) setExposureYn(branchDetailInfo.exposureYn);
  }, [branchDetailInfo]);

  const handleExposureToggle = async () => {
    const newValue = exposureYn === 'Y' ? 'N' : 'Y';
    if (!branchId) {
      if (newValue === 'Y') {
        setExposureConfirmOpen(true);
      } else {
        setExposureYn(newValue);
      }
      return;
    }
    setExposureYn(newValue);
    try {
      await toggleBranchExposure(user.token, branchId, newValue);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      setExposureYn(exposureYn);
      toast.error(errorMessages(error));
    }
  };

  const handleExposureConfirm = () => {
    setExposureYn(exposureYn === 'Y' ? 'N' : 'Y');
    setExposureConfirmOpen(false);
  };

  // 저장된 입장시간을 먼저 불러온 뒤 모달을 연다 (토글/요일별 시간 프리필용)
  const handleOpenEntranceModal = async () => {
    if (branchId && user.token) {
      try {
        setEntranceSetting(await getBranchEntranceTime(branchId, user.token));
      } catch (error) {
        console.error('Failed to fetch entrance time:', error);
        setEntranceSetting(null);
      }
    }
    setEntranceModalOpen(true);
  };

  // 입장 시간 설정 저장 — 저장 시 백엔드가 UBio 동기화까지 자동 수행
  const handleSaveEntrance = async payload => {
    try {
      await updateBranchEntranceTime(payload, branchId, user.token);
      setEntranceSetting(payload);
      toast.success(t('Successfully updated'));
      setEntranceModalOpen(false);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const INIT_DATA = {
    centerId: '',
    branchName: '',
    branchDescription: '',
    postalCode: '',
    city: '',
    address: '',
    addressDetail: '',
    workHoursInfoList: [
      { dayOfWeek: 'MONDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'TUESDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'WEDNESDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'THURSDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'FRIDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'SATURDAY', openTime: '09:00', closeTime: '23:00', open: true },
      { dayOfWeek: 'SUNDAY', openTime: '09:00', closeTime: '23:00', open: false },
    ],
    reservationPolicy: '',
    facilityInfoList: [],
    branchDetailDescription: '',
    addressSame: true,
    regularPrice: 0,
    discount: 0,
    finalPrice: 0,
    images: [],
    video: null,
    videoThumbnail: null,
  };
  const [data, setData] = useState(INIT_DATA);

  // 평일/주말 일괄 적용 상태
  const [applyWeekday, setApplyWeekday] = useState(false);

  // 휴무일 설정 상태
  const [holidayExpanded, setHolidayExpanded] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // 시설 안내 초기값 메모이제이션 (불필요한 리렌더링 방지)
  const facilityInitialValues = useMemo(
    () => data?.facilityInfoList?.map(item => item.facilityName) || [],
    [data?.facilityInfoList],
  );
  const [applyWeekend, setApplyWeekend] = useState(false);

  const handleToggleOpen = (day, isOpen) => {
    setData(prevData => {
      const updatedWorkHoursInfoList = prevData.workHoursInfoList.map(item =>
        item.dayOfWeek === day ? { ...item, open: isOpen } : item,
      );
      return { ...prevData, workHoursInfoList: updatedWorkHoursInfoList };
    });
    const prev_data = getValues('workHoursInfoList');
    const updatedWorkHoursInfoList = prev_data?.map(item =>
      item.dayOfWeek === day ? { ...item, open: isOpen } : item,
    );
    setValue('workHoursInfoList', updatedWorkHoursInfoList);
  };

  // 서울 성동구 옥수동 560

  const handleTimeChange = (day, field, value) => {
    setData(prevData => {
      const updatedWorkHoursInfoList = prevData.workHoursInfoList.map(item =>
        item.dayOfWeek === day ? { ...item, [field]: value } : item,
      );
      return { ...prevData, workHoursInfoList: updatedWorkHoursInfoList };
    });
    const prev_data = getValues('workHoursInfoList');
    const updatedWorkHoursInfoList = prev_data?.map(item =>
      item.dayOfWeek === day ? { ...item, [field]: value } : item,
    );
    setValue('workHoursInfoList', updatedWorkHoursInfoList);
  };

  // 평일 일괄 적용
  const handleApplyWeekday = checked => {
    setApplyWeekday(checked);
    if (checked) {
      const mondayData = data.workHoursInfoList.find(item => item.dayOfWeek === 'MONDAY');
      if (mondayData) {
        const weekdays = ['TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
        setData(prevData => {
          const updatedWorkHoursInfoList = prevData.workHoursInfoList.map(item => {
            if (weekdays.includes(item.dayOfWeek)) {
              return { ...item, openTime: mondayData.openTime, closeTime: mondayData.closeTime, open: mondayData.open };
            }
            return item;
          });
          return { ...prevData, workHoursInfoList: updatedWorkHoursInfoList };
        });
        const prev_data = getValues('workHoursInfoList');
        const updatedWorkHoursInfoList = prev_data?.map(item => {
          if (weekdays.includes(item.dayOfWeek)) {
            return { ...item, openTime: mondayData.openTime, closeTime: mondayData.closeTime, open: mondayData.open };
          }
          return item;
        });
        setValue('workHoursInfoList', updatedWorkHoursInfoList);
      }
    }
  };

  // 주말 일괄 적용
  const handleApplyWeekend = checked => {
    setApplyWeekend(checked);
    if (checked) {
      const saturdayData = data.workHoursInfoList.find(item => item.dayOfWeek === 'SATURDAY');
      if (saturdayData) {
        setData(prevData => {
          const updatedWorkHoursInfoList = prevData.workHoursInfoList.map(item => {
            if (item.dayOfWeek === 'SUNDAY') {
              return {
                ...item,
                openTime: saturdayData.openTime,
                closeTime: saturdayData.closeTime,
                open: saturdayData.open,
              };
            }
            return item;
          });
          return { ...prevData, workHoursInfoList: updatedWorkHoursInfoList };
        });
        const prev_data = getValues('workHoursInfoList');
        const updatedWorkHoursInfoList = prev_data?.map(item => {
          if (item.dayOfWeek === 'SUNDAY') {
            return {
              ...item,
              openTime: saturdayData.openTime,
              closeTime: saturdayData.closeTime,
              open: saturdayData.open,
            };
          }
          return item;
        });
        setValue('workHoursInfoList', updatedWorkHoursInfoList);
      }
    }
  };

  // 신규 매장(브랜치명 없음)일 때만 center 값으로 자동 프리필. 기존 매장은 저장값 유지.
  const hasBranch = !!branchDetailInfo?.branchName;
  const bizInfoTemplate =
    `사업자명: ${centerDetailInfo?.businessName ?? ''}\n` +
    `사업자번호: ${centerDetailInfo?.businessNumber ?? ''}\n` +
    `대표자: ${managerInfo?.name ?? ''}\n` +
    `대표번호: ${centerDetailInfo?.representativeNumber ?? ''}`;

  const defaultValues = useMemo(
    () => ({
      branchName: branchDetailInfo?.branchName || centerDetailInfo?.businessName || '',
      branchDescription: branchDetailInfo?.branchDescription || (hasBranch ? '' : bizInfoTemplate),
      branchDetailDescription: branchDetailInfo?.branchDetailDescription || '',
      address: branchDetailInfo?.address || (hasBranch ? '' : centerDetailInfo?.address || ''),
      addressDetail: branchDetailInfo?.addressDetail || '',
      reservationPolicy: branchDetailInfo?.reservationPolicy || '',
      facilityPoint: branchDetailInfo?.facilityPoint || [],
      workHoursInfoList: branchDetailInfo?.workHoursInfoList?.length
        ? branchDetailInfo.workHoursInfoList
        : data.workHoursInfoList,
      addressSame: branchDetailInfo?.address ? false : branchDetailInfo?.addressSame || true,
      regularPrice: branchDetailInfo?.regularPrice || 0,
      discount: branchDetailInfo?.discount || 0,
      finalPrice: branchDetailInfo?.finalPrice || 0,
      ...SPORT_TYPES.reduce((acc, sport) => {
        acc[sport.key] = Array.isArray(branchDetailInfo?.sportTypes)
          ? branchDetailInfo.sportTypes.includes(sport.enumKey)
          : false;
        return acc;
      }, {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchDetailInfo, sportTypes, centerDetailInfo, managerInfo],
  );

  const { handleSubmit, control, setValue, getValues, watch, reset, trigger } = useForm({ defaultValues });

  // branchDetailInfo가 비동기로 도착했을 때 RHF 폼 값을 다시 채움
  // (useForm은 defaultValues를 마운트 시점에만 읽기 때문에, 첫 진입 시 데이터가 없으면 폼이 빈 채로 굳어버림)
  useEffect(() => {
    // 기존 매장은 branchDetailInfo, 신규 매장은 center 값으로 프리필하기 위해 둘 중 하나라도 도착하면 리셋
    if (branchDetailInfo || centerDetailInfo) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchDetailInfo, centerDetailInfo, managerInfo]);

  const open = useDaumPostcodePopup(scriptUrl);

  const handleComplete = async data => {
    setCity(data.sigungu);

    if (data?.userSelectedType == 'R') {
      setValue('address', data.address);
    } else {
      setValue('address', data.jibunAddress);
    }

    setPostalCode(data.zonecode);
    await getCoordinatesByAddress(data.addressEnglish).then(nominatiumData => {
      setValue('latitude', nominatiumData[0]?.coordinate.latitude);
      setValue('longitude', nominatiumData[0]?.coordinate.longitude);
    });
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  const checked = (isChecked, name) => {
    setValue(name, isChecked);

    if (name !== 'addressSame') {
      // SPORT_TYPES에 정의된 enumKey를 사용, 없으면 fallback으로 name.toUpperCase()
      const sportConfig = SPORT_TYPES.find(s => s.key === name);
      const enumKey = sportConfig ? sportConfig.enumKey : name.toUpperCase();

      setSportTypes(prevValues => {
        const safePrevValues = Array.isArray(prevValues) ? prevValues : [];

        if (isChecked) {
          if (!safePrevValues.includes(enumKey)) {
            return [...safePrevValues, enumKey];
          }
        } else {
          return safePrevValues.filter(element => element !== enumKey);
        }

        return safePrevValues;
      });
    } else {
      if (isChecked) {
        setValue('address', centerDetailInfo.address);
      } else {
        setValue('address', '');
      }
      trigger('address');
    }
  };

  const navigate = useNavigate();

  const submit = async values => {
    setLoading(true);
    const formData = new FormData();

    Object.keys(values).forEach(key => {
      if (key !== 'facilityPoint' || key !== 'workHoursInfoList') {
        formData.append(key, values[key]);
      }
    });

    (Array.isArray(values.facilityPoint) ? values.facilityPoint : []).forEach((item, index) => {
      formData.append(`facilityInfoList[${index}].facilityName`, item);
    });

    if (videoFile.file) {
      formData.append('video', videoFile.file);
    } else if (videoFile.url) {
      formData.append('videoString', videoFile.url === null ? null : videoFile.url);
    }

    if (videoThumbnail.file) {
      formData.append('videoThumbnail', videoThumbnail.file);
    } else if (videoThumbnail.url) {
      formData.append('videoThumbnailString', videoThumbnail.url === null ? null : videoThumbnail.url);
    }

    formData.append('centerId', id);
    formData.append('postalCode', postalCode);
    formData.append('city', city);
    formData.delete('workHoursInfoList');
    formData.delete('discount');
    formData.delete('finalPrice');
    formData.delete('regularPrice');
    formData.append('discount', Number(values.discount.toString().replace(/,/g, '')));
    formData.append('finalPrice', finalPrice);
    formData.append('regularPrice', Number(values.regularPrice.toString().replace(/,/g, '')));
    values.workHoursInfoList.forEach((workHoursInfo, index) => {
      formData.append(`workHoursInfoList[${index}].dayOfWeek`, workHoursInfo.dayOfWeek);
      formData.append(
        `workHoursInfoList[${index}].openTime`,
        workHoursInfo.openTime === null ? '' : workHoursInfo.openTime,
      );
      formData.append(
        `workHoursInfoList[${index}].closeTime`,
        workHoursInfo.closeTime === null ? '' : workHoursInfo.closeTime,
      );
      formData.append(`workHoursInfoList[${index}].open`, workHoursInfo.open);
    });

    if (!branchId) {
      data?.images.forEach((image, index) => {
        if (image.file) {
          formData.append(`images[${index}]`, image.file);
        }
      });
    } else {
      data?.images.forEach((image, index) => {
        if (image.file) {
          formData.append(`images[${index}].image`, image.file);
        } else if (image.url) {
          formData.append(`images[${index}].imageString`, image.url);
        }
      });
    }

    sportTypes.length > 0
      ? sportTypes?.forEach((sportType, index) => {
          formData.append(`sportTypes[${index}]`, sportType);
        })
      : formData.append(`sportTypes`, []);
    try {
      const year = calendarDate.getFullYear();
      if (!branchId) {
        const newBranch = await createBranch(formData, user.token);
        // 휴무일 저장
        if (selectedHolidays.length > 0 && newBranch?.branchId) {
          await updateBranchCloseDays(selectedHolidays, newBranch.branchId, year, user.token);
        }
        toast.success(t('Successfully created'));
        queryClient.invalidateQueries({ queryKey: ['centerDetail', id] });
        navigate('/store');
      } else {
        await updateBranch(formData, branchId, user.token);
        // 휴무일 저장
        await updateBranchCloseDays(selectedHolidays, branchId, year, user.token);
        toast.success(t('Successfully updated'));
        queryClient.invalidateQueries({ queryKey: ['centerDetail', id] });
        navigate('/store');
      }
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(prevData => ({
      ...prevData,
      ...branchDetailInfo,
      branchDetailDescription: branchDetailInfo?.branchDetailDescription || '',
      workHoursInfoList: branchDetailInfo?.workHoursInfoList?.length
        ? branchDetailInfo.workHoursInfoList
        : prevData.workHoursInfoList,
      images:
        branchDetailInfo?.images.length > 0
          ? branchDetailInfo.images.map(element => ({
              url: element,
              file: null,
            }))
          : [],
    }));

    setSportTypes(branchDetailInfo?.sportTypes ? branchDetailInfo?.sportTypes : []);
    setVideoFile({ file: null, url: branchDetailInfo?.video });
    setVideoThumbnail({ file: null, url: branchDetailInfo?.videoThumbnail });
    setFinalPrice(branchDetailInfo?.finalPrice || 0);
  }, [branchDetailInfo]);

  // 기존 휴무일 불러오기
  useEffect(() => {
    const fetchClosedDays = async () => {
      if (branchId && user.token) {
        try {
          const year = calendarDate.getFullYear();
          const closedDays = await getBranchCloseDays(branchId, year, user.token);
          if (closedDays && Array.isArray(closedDays)) {
            setSelectedHolidays(closedDays.sort());
          }
        } catch (error) {
          console.error('Failed to fetch closed days:', error);
        }
      }
    };
    fetchClosedDays();
  }, [branchId, user.token, calendarDate.getFullYear()]);

  const [videoFile, setVideoFile] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);

  const deleteVideo = e => {
    e.preventDefault();
    setVideoFile({ file: null, url: null });

    const fileInput = document.getElementById('video');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const deleteVideoThumbnail = e => {
    e.preventDefault();
    setVideoThumbnail({ file: null, url: null });

    const fileInput = document.getElementById('video-thumbnail');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUploadVideo = async event => {
    try {
      const file = event.target.files[0];
      if (file) {
        const videoURL = URL.createObjectURL(file);
        setVideoFile({ file: file, url: videoURL });
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleUploadVideoThumbnail = async event => {
    try {
      const file = event.target.files[0];
      if (file) {
        const imageURL = URL.createObjectURL(file);
        setVideoThumbnail({ file: file, url: imageURL });
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleSelect = id => {
    setSelected(id);
  };

  const [selected, setSelected] = useState();

  const handleUploadTicketImages = async e => {
    try {
      const loadedImage = await imageValidation(e.target.files[0]);
      setData({
        ...branchDetailInfo,
        images: data.images.map((obj, index) => {
          if (index === selected) {
            return { ...obj, file: loadedImage };
          }
          return obj;
        }),
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const deleteImage = index => {
    setData({
      ...data,
      images: data.images.filter((_, i) => i !== index),
    });
  };

  // 이미지 캐러셀 스크롤 제어
  const imageScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = imageScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth);
  };

  useEffect(() => {
    updateScrollButtons();
    const el = imageScrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [data.images, subTab]);

  const scrollImages = direction => {
    const el = imageScrollRef.current;
    if (!el) return;
    // 이미지 한 장(308) + gap(8)
    el.scrollBy({ left: direction * 316, behavior: 'smooth' });
  };

  async function uploadImages(e) {
    const files = e.target.files;
    const currentLength = [...data.images, ...files].length;
    const imageLimit = 7;

    if (currentLength > imageLimit) {
      alert(`You can only upload up to ${imageLimit} images.`);
      return;
    }

    const images = [];
    for (const file of files) {
      try {
        const loadedImage = await imageValidation(file);
        images.push({ file: loadedImage, url: loadedImage.url });
      } catch (error) {
        toast.error(errorMessages(error));
      }
    }

    setData({
      ...data,
      images: [...data?.images, ...images],
    });
  }

  useEffect(() => {
    if (!lastChangedField) return;

    const regularPrice = watch('regularPrice');
    const discount = watch('discount');
    const finalPrice = watch('finalPrice');

    const regularPriceNum = Number(regularPrice?.toString().replace(/,/g, '') || 0);
    const discountRateNum = Number(discount?.toString().replace(/,/g, '') || 0);
    const finalPriceNum = Number(finalPrice?.toString().replace(/,/g, '') || 0);

    if (lastChangedField === 'regularPrice' && regularPriceNum > 0) {
      setValue('finalPrice', regularPriceNum);
      setValue('discount', 0);
      setFinalPrice(regularPriceNum);
    } else if (lastChangedField === 'discount' && regularPriceNum > 0) {
      if (discountRateNum > 100) {
        toast.error('할인율은 100%를 초과할 수 없습니다');
        setLastChangedField(null);
        return;
      }

      if (discountRateNum < 0) {
        toast.error('할인율은 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }

      const calculatedFinalPrice = Math.round(regularPriceNum - (regularPriceNum * discountRateNum) / 100);
      setValue('finalPrice', calculatedFinalPrice);
      setFinalPrice(calculatedFinalPrice);
    } else if (lastChangedField === 'finalPrice' && regularPriceNum > 0) {
      if (finalPriceNum > regularPriceNum) {
        toast.error('최종가는 정상가보다 클 수 없습니다');
        setLastChangedField(null);
        return;
      }

      if (finalPriceNum < 0) {
        toast.error('최종가는 음수가 될 수 없습니다');
        setLastChangedField(null);
        return;
      }

      let calculatedDiscountRate = Math.round(((regularPriceNum - finalPriceNum) / regularPriceNum) * 100);
      if (finalPriceNum > 0 && calculatedDiscountRate >= 100) {
        calculatedDiscountRate = 99;
      }
      setValue('discount', calculatedDiscountRate);
      setFinalPrice(finalPriceNum);
    }

    setLastChangedField(null);
  }, [lastChangedField]);

  const handleRegularPriceChange = () => {
    setLastChangedField('regularPrice');
  };

  const handleDiscountRateChange = () => {
    setLastChangedField('discount');
  };

  const handleFinalPriceChange = () => {
    setLastChangedField('finalPrice');
  };

  // 시간 옵션 생성 (00 ~ 23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  // 분 옵션 생성 (5분 단위)
  const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  const dayLabels = {
    MONDAY: '월요일',
    TUESDAY: '화요일',
    WEDNESDAY: '수요일',
    THURSDAY: '목요일',
    FRIDAY: '금요일',
    SATURDAY: '토요일',
    SUNDAY: '일요일',
  };

  // 달력 관련 함수들
  const getCalendarDays = date => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    const startDayOfWeek = firstDay.getDay();

    // 이전 달 날짜들
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }

    // 현재 달 날짜들
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // 다음 달 날짜들 (6주 채우기)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const formatDateString = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = date => {
    const dateString = formatDateString(date);
    if (selectedHolidays.includes(dateString)) {
      setSelectedHolidays(prev => prev.filter(d => d !== dateString));
    } else {
      setSelectedHolidays(prev => [...prev, dateString].sort());
    }
  };

  const removeHoliday = dateString => {
    setSelectedHolidays(prev => prev.filter(d => d !== dateString));
  };

  const goToPrevMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const calendarDays = getCalendarDays(calendarDate);
  const weekDayHeaders = ['일', '월', '화', '수', '목', '금', '토'];

  // 공휴일 목록 (양력 기준)
  const publicHolidays = [
    { name: '신정', month: 1, day: 1 },
    { name: '삼일절', month: 3, day: 1 },
    { name: '어린이날', month: 5, day: 5 },
    { name: '현충일', month: 6, day: 6 },
    { name: '광복절', month: 8, day: 15 },
    { name: '개천절', month: 10, day: 3 },
    { name: '한글날', month: 10, day: 9 },
    { name: '크리스마스', month: 12, day: 25 },
  ];

  // 공휴일 메뉴 상태
  const [holidayMenuAnchor, setHolidayMenuAnchor] = useState(null);

  // 공휴일 추가
  const addPublicHoliday = holiday => {
    const year = calendarDate.getFullYear();
    const dateString = `${year}-${String(holiday.month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
    if (!selectedHolidays.includes(dateString)) {
      setSelectedHolidays(prev => [...prev, dateString].sort());
    }
    setHolidayMenuAnchor(null);
  };

  // 모든 공휴일 추가
  const addAllPublicHolidays = () => {
    const year = calendarDate.getFullYear();
    const newHolidays = publicHolidays.map(
      h => `${year}-${String(h.month).padStart(2, '0')}-${String(h.day).padStart(2, '0')}`,
    );
    setSelectedHolidays(prev => {
      const combined = [...new Set([...prev, ...newHolidays])];
      return combined.sort();
    });
    setHolidayMenuAnchor(null);
  };

  // 탭 스타일
  const tabStyle = {
    minWidth: 'auto',
    padding: '8px 20px',
    borderRadius: '20px',
    marginRight: '8px',
    border: '1px solid #ddd',
    color: '#666',
    fontWeight: 500,
    fontSize: '14px',
    '&.Mui-selected': {
      backgroundColor: '#4AB3BC',
      color: '#fff',
      border: '1px solid #4AB3BC',
    },
  };

  return (
    <>
      {/* 서브 탭 네비게이션 */}
      <Box sx={{ mt: '20px', mb: '30px' }}>
        <Tabs
          value={subTab}
          onChange={(e, newValue) => setSubTab(newValue)}
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{ minHeight: 'auto' }}>
          <Tab label="매장 소개" sx={tabStyle} />
          <Tab label="매장 영업시간" sx={tabStyle} />
        </Tabs>
      </Box>

      {/* 앱 노출 토글 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: '20px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333', mr: 1 }}>앱 노출</Typography>
        <Switch
          size="small"
          checked={exposureYn === 'Y'}
          onChange={handleExposureToggle}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
          }}
        />
        <Typography sx={{ ml: 0.5, fontSize: '13px', color: '#666' }}>
          {exposureYn === 'Y' ? '노출' : '숨김'}
        </Typography>
      </Box>

      <ConfirmModal
        open={exposureConfirmOpen}
        onClose={() => setExposureConfirmOpen(false)}
        onConfirm={handleExposureConfirm}
        message={`아직 매장 정보가 등록되지 않았습니다.\n매장 정보 입력 후 저장 버튼을 클릭하면\n앱에 노출됩니다.\n\n진행하시겠습니까?`}
        highlightText="진행"
        highlightColor="#4AB3BC"
        cancelText="취소"
        confirmText="진행"
        confirmColor="#4AB3BC"
      />

      {/* 매장 소개 탭 */}
      {subTab === 0 && (
        <Box sx={{ display: 'flex', gap: '40px' }}>
          {/* 왼쪽 컬럼 */}
          <Box sx={{ flex: 1, maxWidth: '500px' }}>
            {/* 매장명 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '8px' }}>매장명</Typography>
              <FormInputText
                control={control}
                name="branchName"
                rules={{ required: true }}
                placeholder="지점명을 입력해 주세요"
                sx={{ width: '100%' }}
                color="info"
              />
            </Box>

            {/* 소개 요약 */}
            <Box sx={{ mb: '20px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>사업자 정보</Typography>
                <Typography sx={{ fontSize: '12px', color: '#888' }}>
                  ({watch('branchDescription')?.length ?? 0}/100)
                </Typography>
              </Box>
              <FormInputText
                control={control}
                name="branchDescription"
                placeholder="지점에 대해 간단하게 소개해 주세요."
                inputProps={{ maxLength: '100' }}
                rows={2}
                multiline
                sx={{ width: '100%' }}
              />
            </Box>

            {/* 주소 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '8px' }}>주소</Typography>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <FormInputText
                  control={control}
                  name="address"
                  placeholder="주소를 검색해 주세요"
                  sx={{ flex: 1 }}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  onClick={handleClick}
                  sx={{
                    backgroundColor: '#555',
                    color: '#fff',
                    borderRadius: '5px',
                    px: '16px',
                    '&:hover': { backgroundColor: '#333' },
                  }}>
                  주소검색
                </Button>
              </Box>
            </Box>

            {/* 상세 주소 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '8px' }}>상세 주소</Typography>
              <FormInputText
                control={control}
                name="addressDetail"
                placeholder="상세 주소를 입력해 주세요"
                sx={{ width: '100%' }}
              />
            </Box>

            {/* 이용 안내 */}
            <Box sx={{ mb: '20px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>이용 안내</Typography>
                <Typography sx={{ fontSize: '12px', color: '#888' }}>
                  ({watch('reservationPolicy')?.length ?? 0}/200)
                </Typography>
              </Box>
              <FormInputText
                control={control}
                name="reservationPolicy"
                placeholder="매장이용 안내를 입력해 주세요."
                inputProps={{ maxLength: '200' }}
                rows={4}
                multiline
                sx={{ width: '100%' }}
              />
            </Box>

            {/* 시설 안내 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '8px' }}>
                시설 안내
                <span style={{ fontSize: '12px', color: '#888', fontWeight: 400, marginLeft: '8px' }}>
                  * 복수선택 가능
                </span>
              </Typography>
              <FormInputMultiCheckbox
                name="facilityPoint"
                control={control}
                setValue={setValue}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  label: {
                    position: 'relative',
                    margin: '0 8px 8px 0',
                    padding: '8px 16px',
                    background: '#FAFBFC',
                    transition: '0.3s',
                    borderRadius: '20px',
                    border: '1px solid #ddd',
                    color: '#666',
                    '&.active': {
                      background: '#0F7F85',
                      border: '1px solid #0F7F85',
                    },
                    '&.active span': {
                      color: '#fff !important',
                    },
                    span: {
                      lineHeight: 1,
                      fontSize: '13px',
                    },
                    div: {
                      display: 'none',
                    },
                  },
                }}
                options={facilities.map(item => ({
                  label: t(item.facilityName),
                  value: item.facilityName,
                }))}
                initialValues={facilityInitialValues}
              />
            </Box>

            {/* 대표 운동 종목 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '12px' }}>
                대표 운동 종목
                <span style={{ fontSize: '12px', color: '#888', fontWeight: 400, marginLeft: '8px' }}>
                  * 복수선택 가능
                </span>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {SPORT_TYPES.map(sport => (
                  <FormControlLabel
                    key={sport.key}
                    control={
                      <CustomCheckbox
                        name={sport.key}
                        checked={watch(sport.key)}
                        onChange={e => checked(e.target.checked, sport.key)}
                      />
                    }
                    label={sport.label}
                    sx={{ width: `${sport.width}px`, m: 0 }}
                  />
                ))}
              </Box>
            </Box>

            {/* 대표 상품 정보 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '12px' }}>
                대표 상품 정보
                <span style={{ fontSize: '11px', color: '#888', fontWeight: 400, marginLeft: '8px' }}>
                  * 회원 앱에 노출될 대표 이용권/수강권 가격을 입력해 주세요.
                </span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>정상가</Typography>
                  <FormInputText
                    control={control}
                    name="regularPrice"
                    sx={{ width: '100px' }}
                    placeholder="0"
                    type="tel"
                    onChange={handleRegularPriceChange}
                  />
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>원</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>할인율</Typography>
                  <FormInputText
                    control={control}
                    name="discount"
                    sx={{ width: '60px' }}
                    placeholder="0"
                    type="tel"
                    disabled={true}
                  />
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>판매가격</Typography>
                  <FormInputText
                    control={control}
                    name="finalPrice"
                    sx={{ width: '100px' }}
                    placeholder="0"
                    type="tel"
                    onChange={handleFinalPriceChange}
                  />
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>원</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 오른쪽 컬럼 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* 매장 대표 이미지 설정 */}
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '12px' }}>
                매장 대표 이미지 설정
                <span style={{ fontSize: '11px', color: '#888', fontWeight: 400, marginLeft: '8px' }}>
                  * 4:3, 1200*900px 사이즈 권장
                </span>
                <span style={{ fontSize: '11px', color: '#0F7F85', fontWeight: 500, marginLeft: '4px' }}>
                  (최대 7장)
                </span>
              </Typography>
              <Box sx={{ position: 'relative' }}>
                {canScrollLeft && (
                  <Button
                    onClick={() => scrollImages(-1)}
                    sx={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      minWidth: 'unset',
                      width: '36px',
                      height: '36px',
                      p: 0,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '50%',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      '&:hover': { backgroundColor: '#fff' },
                    }}>
                    <ChevronLeftIcon sx={{ color: '#333' }} />
                  </Button>
                )}
                {canScrollRight && (
                  <Button
                    onClick={() => scrollImages(1)}
                    sx={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      minWidth: 'unset',
                      width: '36px',
                      height: '36px',
                      p: 0,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      borderRadius: '50%',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      '&:hover': { backgroundColor: '#fff' },
                    }}>
                    <ChevronRightIcon sx={{ color: '#333' }} />
                  </Button>
                )}
                <Box
                  ref={imageScrollRef}
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    pb: '8px',
                    '&::-webkit-scrollbar': {
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#ccc',
                      borderRadius: '3px',
                    },
                  }}>
                  {/* 이미지 업로드 버튼 */}
                  <Box
                    component="label"
                    htmlFor="image-upload"
                    sx={{
                      minWidth: '160px',
                      width: '160px',
                      height: '308px',
                      border: '1px solid #D9D9D9',
                      borderRadius: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}>
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        mb: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 16V8M12 8L9 11M12 8L15 11"
                          stroke="#0F7F85"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15"
                          stroke="#0F7F85"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#0F7F85', fontWeight: 500 }}>이미지 업로드</Typography>
                    <input hidden accept="image/*" multiple type="file" id="image-upload" onChange={uploadImages} />
                  </Box>

                  {/* 업로드된 이미지들 */}
                  {data.images?.map((img, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        minWidth: '200px',
                        width: '308px',
                        height: '308px',
                        flexShrink: 0,
                      }}>
                      <img
                        src={img.url ?? (window.URL || window.webkitURL).createObjectURL(img.file)}
                        alt={`이미지 ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                      <Button
                        onClick={() => deleteImage(index)}
                        sx={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          minWidth: 'unset',
                          p: '4px',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          borderRadius: '50%',
                          '&:hover': { backgroundColor: '#fff' },
                        }}>
                        <DeleteForeverIcon sx={{ fontSize: '16px', color: '#666' }} />
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* 매장 상세 소개 */}
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', mb: '8px' }}>
                매장 상세 소개
              </Typography>
              <FormInputCEditor
                setValue={setValue}
                sx={{ width: '100%', height: '100%' }}
                control={control}
                data={data?.branchDetailDescription ? data?.branchDetailDescription : ''}
                name="branchDetailDescription"
                setLoading={setLoading}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* 매장 영업시간 탭 */}
      {subTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>영업시간 설정</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenEntranceModal}
              sx={{
                color: '#0F7F85',
                borderColor: '#0F7F85',
                fontSize: '13px',
                '&:hover': { borderColor: '#0F7F85', backgroundColor: 'rgba(15, 127, 133, 0.04)' },
              }}>
              입장 시간 설정
            </Button>
          </Box>

          {data?.workHoursInfoList?.map((workHour, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: '12px',
                gap: '16px',
              }}>
              {/* 요일 */}
              <Typography sx={{ width: '50px', fontSize: '14px', fontWeight: 500 }}>
                {dayLabels[workHour.dayOfWeek]}
              </Typography>

              {/* 영업/휴무 토글 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100px' }}>
                <Typography sx={{ fontSize: '13px', color: workHour.open ? '#4AB3BC' : '#888' }}>
                  {workHour.open ? '영업' : '휴무'}
                </Typography>
                <Switch
                  checked={workHour.open}
                  onChange={e => handleToggleOpen(workHour.dayOfWeek, e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4AB3BC',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4AB3BC',
                    },
                  }}
                />
              </Box>

              {/* 시작 시간 */}
              <Select
                value={workHour.openTime?.split(':')[0] || '09'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'openTime',
                    `${e.target.value}:${workHour.openTime?.split(':')[1] || '00'}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{ width: '80px', backgroundColor: workHour.open ? '#fff' : '#f5f5f5' }}>
                {hourOptions.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ color: '#888' }}>:</Typography>
              <Select
                value={workHour.openTime?.split(':')[1] || '00'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'openTime',
                    `${workHour.openTime?.split(':')[0] || '09'}:${e.target.value}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{ width: '80px', backgroundColor: workHour.open ? '#fff' : '#f5f5f5' }}>
                {minuteOptions.map(min => (
                  <MenuItem key={min} value={min}>
                    {min}
                  </MenuItem>
                ))}
              </Select>

              <Typography sx={{ color: '#888' }}>~</Typography>

              {/* 종료 시간 */}
              <Select
                value={workHour.closeTime?.split(':')[0] || '23'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'closeTime',
                    `${e.target.value}:${workHour.closeTime?.split(':')[1] || '00'}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{ width: '80px', backgroundColor: workHour.open ? '#fff' : '#f5f5f5' }}>
                {hourOptions.map(hour => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ color: '#888' }}>:</Typography>
              <Select
                value={workHour.closeTime?.split(':')[1] || '00'}
                onChange={e =>
                  handleTimeChange(
                    workHour.dayOfWeek,
                    'closeTime',
                    `${workHour.closeTime?.split(':')[0] || '23'}:${e.target.value}`,
                  )
                }
                disabled={!workHour.open}
                size="small"
                IconComponent={KeyboardArrowDownIcon}
                sx={{ width: '80px', backgroundColor: workHour.open ? '#fff' : '#f5f5f5' }}>
                {minuteOptions.map(min => (
                  <MenuItem key={min} value={min}>
                    {min}
                  </MenuItem>
                ))}
              </Select>
              {workHour.open && isOvernight(workHour.openTime, workHour.closeTime) && (
                <NextDayBadge>익일</NextDayBadge>
              )}

              {/* 일괄 적용 체크박스 */}
              {workHour.dayOfWeek === 'MONDAY' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applyWeekday}
                      onChange={e => handleApplyWeekday(e.target.checked)}
                      sx={{
                        color: '#0F7F85',
                        '&.Mui-checked': { color: '#0F7F85' },
                      }}
                    />
                  }
                  label="평일(월-금) 일괄 적용"
                  sx={{ ml: '16px', '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                />
              )}
              {workHour.dayOfWeek === 'SATURDAY' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={applyWeekend}
                      onChange={e => handleApplyWeekend(e.target.checked)}
                      sx={{
                        color: '#0F7F85',
                        '&.Mui-checked': { color: '#0F7F85' },
                      }}
                    />
                  }
                  label="주말(토, 일) 일괄 적용"
                  sx={{ ml: '16px', '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                />
              )}
            </Box>
          ))}

          {/* 휴무일 설정 */}
          <Box sx={{ mt: '30px' }}>
            {/* 아코디언 헤더 */}
            <Box
              onClick={() => setHolidayExpanded(!holidayExpanded)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: '16px',
                px: '20px',
                border: '1px solid #ddd',
                borderRadius: holidayExpanded ? '8px 8px 0 0' : '8px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                '&:hover': { backgroundColor: '#fafafa' },
              }}>
              <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#000' }}>휴무일</Typography>
              <KeyboardArrowDownIcon
                sx={{
                  transform: holidayExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: '#666',
                }}
              />
            </Box>

            {/* 아코디언 콘텐츠 */}
            {holidayExpanded && (
              <Box
                sx={{
                  display: 'flex',
                  border: '1px solid #ddd',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  minHeight: '300px',
                }}>
                {/* 왼쪽: 선택된 휴무일 목록 */}
                <Box
                  sx={{
                    flex: 1,
                    p: '16px',
                    borderRight: '1px solid #ddd',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}>
                  {/* 공휴일 추가 버튼 */}
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={e => setHolidayMenuAnchor(e.currentTarget)}
                      sx={{
                        color: '#0F7F85',
                        borderColor: '#0F7F85',
                        fontSize: '13px',
                        '&:hover': {
                          borderColor: '#0F7F85',
                          backgroundColor: 'rgba(15, 127, 133, 0.04)',
                        },
                      }}>
                      공휴일 추가
                    </Button>
                    <Menu
                      anchorEl={holidayMenuAnchor}
                      open={Boolean(holidayMenuAnchor)}
                      onClose={() => setHolidayMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                      {(() => {
                        const year = calendarDate.getFullYear();
                        const addedCount = publicHolidays.filter(h => {
                          const ds = `${year}-${String(h.month).padStart(2, '0')}-${String(h.day).padStart(2, '0')}`;
                          return selectedHolidays.includes(ds);
                        }).length;
                        const allAdded = addedCount === publicHolidays.length;
                        return (
                          <MenuItem
                            onClick={addAllPublicHolidays}
                            sx={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#0F7F85',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: allAdded ? 'rgba(15, 127, 133, 0.08)' : 'inherit',
                            }}>
                            <span>전체 공휴일 추가 ({calendarDate.getFullYear()}년)</span>
                            <Typography
                              component="span"
                              sx={{ fontSize: '12px', color: allAdded ? '#0F7F85' : '#999', ml: 1 }}>
                              {addedCount}/{publicHolidays.length}
                            </Typography>
                          </MenuItem>
                        );
                      })()}
                      <Box sx={{ borderTop: '1px solid #eee', my: '4px' }} />
                      {publicHolidays.map(holiday => {
                        const year = calendarDate.getFullYear();
                        const dateString = `${year}-${String(holiday.month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
                        const isAdded = selectedHolidays.includes(dateString);
                        return (
                          <MenuItem
                            key={`${holiday.month}-${holiday.day}`}
                            onClick={() => addPublicHoliday(holiday)}
                            sx={{
                              fontSize: '13px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              color: isAdded ? '#0F7F85' : 'inherit',
                              backgroundColor: isAdded ? 'rgba(15, 127, 133, 0.08)' : 'inherit',
                            }}>
                            <span>
                              {holiday.name} ({holiday.month}.{holiday.day})
                            </span>
                            {isAdded && <CheckIcon sx={{ fontSize: '16px', ml: 1, color: '#0F7F85' }} />}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>

                  {/* 선택된 휴무일 목록 */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignContent: 'flex-start',
                      gap: '8px',
                    }}>
                    {selectedHolidays.map(dateString => (
                      <Box
                        key={dateString}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          px: '12px',
                          py: '6px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          height: 'fit-content',
                        }}>
                        <Typography sx={{ fontSize: '13px', color: '#333' }}>{dateString}</Typography>
                        <Box
                          component="span"
                          onClick={() => removeHoliday(dateString)}
                          sx={{
                            cursor: 'pointer',
                            color: '#999',
                            fontSize: '16px',
                            lineHeight: 1,
                            '&:hover': { color: '#666' },
                          }}>
                          ×
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* 오른쪽: 달력 */}
                <Box sx={{ width: '280px', p: '16px' }}>
                  {/* 달력 헤더 */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: '16px',
                    }}>
                    <Box
                      onClick={goToPrevMonth}
                      sx={{
                        cursor: 'pointer',
                        color: '#0F7F85',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        px: '8px',
                        '&:hover': { opacity: 0.7 },
                      }}>
                      {'<'}
                    </Box>
                    <Typography sx={{ fontWeight: 500, fontSize: '16px' }}>
                      {calendarDate.getFullYear()}. {calendarDate.getMonth() + 1}
                    </Typography>
                    <Box
                      onClick={goToNextMonth}
                      sx={{
                        cursor: 'pointer',
                        color: '#0F7F85',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        px: '8px',
                        '&:hover': { opacity: 0.7 },
                      }}>
                      {'>'}
                    </Box>
                  </Box>

                  {/* 요일 헤더 */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      textAlign: 'center',
                      mb: '8px',
                    }}>
                    {weekDayHeaders.map((day, index) => (
                      <Typography
                        key={day}
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: index === 0 ? '#E57373' : index === 6 ? '#64B5F6' : '#666',
                          py: '4px',
                        }}>
                        {day}
                      </Typography>
                    ))}
                  </Box>

                  {/* 날짜 그리드 */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '2px',
                    }}>
                    {calendarDays.map((dayInfo, index) => {
                      const dateString = formatDateString(dayInfo.date);
                      const isSelected = selectedHolidays.includes(dateString);
                      const dayOfWeek = dayInfo.date.getDay();
                      const isSunday = dayOfWeek === 0;
                      const isSaturday = dayOfWeek === 6;

                      return (
                        <Box
                          key={index}
                          onClick={() => handleDateClick(dayInfo.date)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '32px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            backgroundColor: isSelected ? '#333' : 'transparent',
                            color: isSelected
                              ? '#fff'
                              : !dayInfo.isCurrentMonth
                                ? '#ccc'
                                : isSunday
                                  ? '#E57373'
                                  : isSaturday
                                    ? '#64B5F6'
                                    : '#333',
                            fontSize: '13px',
                            '&:hover': {
                              backgroundColor: isSelected ? '#333' : '#f0f0f0',
                            },
                          }}>
                          {dayInfo.date.getDate()}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* 하단 버튼 */}
      <Divider sx={{ my: '30px' }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          pb: '60px',
        }}>
        <Button
          onClick={() => navigate(-1)}
          sx={{
            px: '32px',
            py: '10px',
            borderRadius: '5px',
            backgroundColor: '#98A6AD',
            color: '#fff',
            '&:hover': { backgroundColor: '#7a8a91' },
          }}>
          취소
        </Button>
        <Button
          onClick={handleSubmit(submit)}
          disabled={loading}
          sx={{
            px: '40px',
            py: '10px',
            borderRadius: '5px',
            backgroundColor: '#0F7F85',
            color: '#fff',
            '&:hover': { backgroundColor: '#0a6066' },
          }}>
          저장
        </Button>
      </Box>
      {loading && <StoreUploadModal />}

      <EntranceTimeModal
        open={entranceModalOpen}
        onClose={() => setEntranceModalOpen(false)}
        workHours={data?.workHoursInfoList}
        initialHolidays={selectedHolidays}
        initialEntranceTime={entranceSetting}
        onSave={handleSaveEntrance}
      />

    </>
  );
}

export default StoreTab2;
